import React, {Component} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {DraftMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {composerDetailsTokenBoxStyle} from '../../../../../libs/shared/ui/src/styles/composerStyles';
import {internalRecipientStyle} from '../../../common/editor/tokens/tokenStyle';
import {ChannelSaver, PickChannelDropdownContainer, SaveChannelOptions} from './pickChannelDropdownContainer';

/*
 * Props.
 */

interface ComposerChannelPickerProps {
  isUserVisitor: boolean;
  message: DraftMessageModel;
  channel: ChannelModel;
  saveChannel: ChannelSaver;
}

/*
 * Style.
 */

const StyledPickerDiv = styled.div`
  ${composerDetailsTokenBoxStyle};
  padding-left: 6px;
  padding-right: 6px;
  margin-right: 4px;

  border-radius: 4px;
  ${internalRecipientStyle}
`;

/*
 * Component.
 */

interface ComposerChannelPickerState {
  resetMessageOptions?: SaveChannelOptions;
}

export class ComposerChannelPicker extends Component<ComposerChannelPickerProps, ComposerChannelPickerState> {
  constructor(props: ComposerChannelPickerProps) {
    super(props);
    this.state = {};
  }

  /*
   * Event handlers.
   */

  private readonly onChannelSelected: ChannelSaver = (options) => {
    const {message, saveChannel} = this.props;

    // If the message has been saved and we're changing the type, confirm before resetting content.
    const willResetMessage = message.updatedAt > 0 && options.shouldResetContent;
    if (willResetMessage) {
      this.setState({resetMessageOptions: options});
      return;
    }

    saveChannel(options);
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        <PopoverCoordinator
          renderButton={(isOpen) => this.renderButton(isOpen)}
          renderModal={(modalProps) => this.renderDropdown(modalProps)}
          isDisabled={this.props.isUserVisitor}
          shouldHoverWhenOpen
        />
        {this.maybeRenderNewMessageTypeDialog()}
      </>
    );
  }

  private renderButton(isOpen: boolean) {
    const {channel} = this.props;

    return <StyledPickerDiv>{channel.displayName}</StyledPickerDiv>;
  }

  private renderDropdown(modalProps: PopoverTransferProps) {
    const {message, channel} = this.props;
    const saveChannel: ChannelSaver = (options) => this.onChannelSelected(options);

    return (
      <PickChannelDropdownContainer
        {...modalProps}
        message={message}
        channel={channel}
        saveChannel={saveChannel}
      />
    );
  }

  private maybeRenderNewMessageTypeDialog() {
    const {saveChannel} = this.props;
    const {resetMessageOptions} = this.state;
    if (!resetMessageOptions) {
      return null;
    }

    const onChannelConfirmed = () => {
      saveChannel(resetMessageOptions);
      this.setState({resetMessageOptions: undefined});
    };

    return (
      <OkCancelDialog
        title={<Trans id="composer-resetmessage-title">Switch channel?</Trans>}
        okButtonLabel={<Trans id="composer-resetmessage-ok">Reset message</Trans>}
        onOkClick={onChannelConfirmed}
        onRequestClose={() => this.setState({resetMessageOptions: undefined})}
      >
        <Trans id="composer-resetmessage-content">
          You are about to switch to a channel of a different type. The content of your message will be reset.
        </Trans>
      </OkCancelDialog>
    );
  }
}
